'use client';

import { Form } from '@statura/ui';
import { PageTitle } from '@statura/ui';
import { useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { STATURA_CARE_LEGAL_EMAIL, STATURA_CARE_PUBLIC_URLS } from '@statura/utils/public-legal';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ExternalLink,
  FileSignature,
  Loader2,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { toastFail } from '@/lib/toast-voice';
import type { CustomerLegalState, CustomerLegalStatusResponse } from '@/lib/customer-legal-shared';

export function CustomerLegalGate({
  state,
  onAccepted,
}: {
  state: CustomerLegalState;
  onAccepted?: (nextState: CustomerLegalState) => void;
}) {
  const router = useRouter();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const pendingDocuments = useMemo(
    () => state.requiredDocuments.filter((document) => !document.isCurrent),
    [state.requiredDocuments],
  );
  const currentDocuments = useMemo(
    () => state.requiredDocuments.filter((document) => document.isCurrent),
    [state.requiredDocuments],
  );

  const allPendingConfirmed = pendingDocuments.every((document) => checked[document.type]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allPendingConfirmed) return;

    setSubmitting(true);
    try {
      const documents = state.requiredDocuments.map((document) => document.type);
      const response = await fetch('/api/customer-legal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documents,
        }),
      });

      const payload = (await response.json().catch(() => ({}))) as
        | CustomerLegalStatusResponse
        | { error?: string };

      if (!response.ok) {
        throw new Error(payload && 'error' in payload ? payload.error : undefined);
      }

      if (!('state' in payload)) {
        throw new Error('Missing customer legal state after acceptance.');
      }

      toast.success('Customer agreement recorded');
      onAccepted?.(payload.state);
      router.refresh();
    } catch (error) {
      toastFail('record customer agreement', error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-4xl items-center justify-center px-4 py-8">
      <div className="w-full max-w-3xl rounded-[28px] border border-border bg-surface-sheet p-8 shadow-card sm:p-10">
        <div className="rounded-3xl border border-status-warning-border bg-status-warning-bg/70 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-surface-sheet shadow-sm">
              <Shield className="h-6 w-6 text-statura-green-accessible" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <PageTitle className="font-heading text-page-title text-text-heading">
                  {state.hasOutdatedAcceptance
                    ? 'Review updated customer terms before continuing'
                    : 'Confirm your organisation’s customer terms'}
                </PageTitle>
                <span className="rounded-full bg-surface-sheet px-3 py-1 text-caption font-semibold uppercase tracking-[0.18em] text-text-muted">
                  Organisation administrator
                </span>
              </div>
              <p className="mt-3 font-body text-body text-text-body">
                Statura records versioned customer legal acceptance for each tenant. A tenant
                administrator must confirm the current commercial terms, data processing schedule,
                subprocessor schedule, and signatory authority before the dashboard can continue.
              </p>
              <p className="mt-2 font-body text-body-sm text-text-muted">
                This confirmation is recorded in your tenant audit history with the accepting user,
                timestamp, IP address, and user agent. It sits alongside any executed order form or
                procurement paperwork for your organisation.
              </p>
            </div>
          </div>
        </div>

        {currentDocuments.length > 0 ? (
          <div className="mt-6 rounded-2xl border border-status-success-border bg-status-success-bg px-4 py-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-status-success-fg" />
              <div>
                <p className="text-body font-medium text-status-success-fg">Already current</p>
                <ul className="mt-2 space-y-1 text-body text-status-success-fg">
                  {currentDocuments.map((document) => (
                    <li key={document.type}>
                      {document.label} v{document.version}
                      {document.acceptedAt
                        ? ` accepted on ${new Date(document.acceptedAt).toLocaleDateString(
                            'en-AU',
                            {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            },
                          )}`
                        : ''}
                      {document.acceptedByName ? ` by ${document.acceptedByName}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : null}

        <Form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {pendingDocuments.map((document) => (
            <label
              key={document.type}
              className="block rounded-2xl border border-border bg-surface-soft px-4 py-4"
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={Boolean(checked[document.type])}
                  onChange={(event) =>
                    setChecked((current) => ({
                      ...current,
                      [document.type]: event.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-control-border text-statura-green-accessible focus:ring-statura-green-accessible"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-body font-semibold text-statura-ink">{document.label}</p>
                    <span className="rounded-full bg-surface-sheet px-2.5 py-0.5 text-caption font-semibold text-text-muted">
                      v{document.version}
                    </span>
                  </div>
                  <p className="mt-1 text-body text-text-muted">{document.summary}</p>
                  {document.url ? (
                    <Link
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-body font-medium text-statura-green-accessible hover:underline"
                    >
                      Review document
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  ) : null}
                  <p className="mt-3 text-body text-statura-ink">{document.acknowledgementLabel}</p>
                </div>
              </div>
            </label>
          ))}

          <div className="rounded-2xl border border-border bg-surface-sheet px-4 py-4 text-body text-text-muted">
            <div className="flex items-start gap-2">
              <Building2 className="mt-0.5 h-4 w-4 text-statura-green-accessible" />
              <p>
                If you are not authorised to bind your organisation, stop here and ask an authorised
                organisation administrator or procurement lead to complete this step instead.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-surface-sheet px-4 py-4 text-body text-text-muted">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-status-warning-fg" />
              <p>
                Need the branded legal pack or a countersigned copy before accepting? Contact{' '}
                <Link
                  href={`mailto:${STATURA_CARE_LEGAL_EMAIL}`}
                  className="font-medium text-statura-green-accessible hover:underline"
                >
                  {STATURA_CARE_LEGAL_EMAIL}
                </Link>{' '}
                or visit{' '}
                <Link
                  href={STATURA_CARE_PUBLIC_URLS.support}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-statura-green-accessible hover:underline"
                >
                  support
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-body text-text-muted">
              This step is only required again when Statura materially updates the customer legal
              pack.
            </div>
            <button
              type="submit"
              disabled={!allPendingConfirmed || submitting}
              className="inline-flex items-center gap-2 rounded-control bg-statura-green-accessible px-4 py-2 text-body font-medium text-white transition hover:bg-statura-green-accessible-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSignature className="h-4 w-4" />
              )}
              {submitting ? 'Recording...' : 'Accept and continue'}
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
}
